/**
 * Core clinical domain types shared by the web app, seed tooling and the database schema.
 * The JSON shapes here are persisted as-is in `medications.doses` / `medications.concentrations`
 * (jsonb), so changes must stay backward compatible or ship with a data migration.
 */

export type ProviderLevel =
  | 'emr'
  | 'emt'
  | 'aemt'
  | 'paramedic'
  | 'critical_care'
  | 'nurse'
  | 'physician';

export const PROVIDER_LEVELS: { value: ProviderLevel; label: string }[] = [
  { value: 'emr', label: 'EMR' },
  { value: 'emt', label: 'EMT' },
  { value: 'aemt', label: 'AEMT' },
  { value: 'paramedic', label: 'Paramedic' },
  { value: 'critical_care', label: 'Critical Care Paramedic' },
  { value: 'nurse', label: 'Nurse' },
  { value: 'physician', label: 'Physician' },
];

/** Population a dose rule is written for. `all` applies regardless of age. */
export type Population = 'adult' | 'pediatric' | 'neonate' | 'all';
export type PatientPopulation = Exclude<Population, 'all'>;

export type MassUnit = 'mcg' | 'mg' | 'g';
export type DoseUnit = MassUnit | 'mEq' | 'units' | 'mL';

export type Route =
  | 'IV'
  | 'IO'
  | 'IM'
  | 'IN'
  | 'PO'
  | 'SL'
  | 'ODT'
  | 'NEB'
  | 'SQ'
  | 'ET'
  | 'PR'
  | 'BUCCAL';

export const ROUTES: Route[] = ['IV', 'IO', 'IM', 'IN', 'PO', 'SL', 'ODT', 'NEB', 'SQ', 'ET', 'PR', 'BUCCAL'];

export type ConcentrationForm = 'solution' | 'tablet' | 'spray' | 'tube' | 'device';

/**
 * A drug concentration. For solutions, `volumeMl` is required and drives volume calculations
 * (e.g. 1 mg / 10 mL). For discrete forms (tablet, spray, device) `amount` is per unit.
 */
export interface Concentration {
  amount: number;
  unit: DoseUnit;
  volumeMl?: number;
  form?: ConcentrationForm;
  label?: string;
}

export type DoseKind = 'fixed' | 'weight' | 'infusion';
export type RateBasis = 'min' | 'hr';

export interface DoseRule {
  id: string;
  indication: string;
  population: Population;
  routes: Route[];
  kind: DoseKind;
  /** Low end of the dose. Per kg when kind = weight (or infusion with perKg). */
  dose: number;
  /** Optional high end of a dose range. */
  doseHigh?: number;
  unit: DoseUnit;
  /** Infusions only: dose is expressed per kg of body weight. */
  perKg?: boolean;
  /** Infusions only: dose is per minute or per hour. */
  rateBasis?: RateBasis;
  /** Absolute cap for a single dose, in `unit`. */
  maxSingle?: number;
  /** Absolute cap for the cumulative dose, in `unit`. */
  maxTotal?: number;
  repeatMinutes?: number;
  maxRepeats?: number;
  concentration?: Concentration;
  /** Inclusive lower weight bound. */
  minWeightKg?: number;
  /** Exclusive upper weight bound. */
  maxWeightKg?: number;
  /** Inclusive lower age bound (years). */
  minAgeYears?: number;
  /** Exclusive upper age bound (years). */
  maxAgeYears?: number;
  administration?: string;
  notes?: string;
  tags?: string[];
}

export interface Medication {
  /** Stable slug, unique within a scope (platform library or organization). */
  id: string;
  genericName: string;
  brandNames: string[];
  drugClass: string;
  mechanism?: string;
  indications: string[];
  contraindications: string[];
  precautions: string[];
  sideEffects: string[];
  onset?: string;
  duration?: string;
  pregnancy?: string;
  highAlert?: boolean;
  controlledSchedule?: string | null;
  /** Provider levels typically credentialed for this medication (varies by state/agency). */
  scope: ProviderLevel[];
  concentrations: Concentration[];
  notes?: string;
  doses: DoseRule[];
}

export type Sex = 'male' | 'female';

export interface Patient {
  weightKg?: number;
  ageYears?: number;
  heightCm?: number;
  sex?: Sex;
  /** Explicit population override; otherwise inferred from age. */
  population?: PatientPopulation;
}
